// oxlint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { Component } from 'react';
import { canPlay, MATCH_URL_YOUTUBE } from '../../../constants';
import { defaultProps } from '../props';
import type { BaseReactPlayerProps } from '../types';
import { callPlayer, getSDK, parseEndTime, parseStartTime } from '../utils';

const SDK_URL = 'https://www.youtube.com/iframe_api';
const SDK_GLOBAL = 'YT';
const SDK_GLOBAL_READY = 'onYouTubeIframeAPIReady';
const MATCH_NOCOOKIE = /youtube-nocookie\.com/;
const NOCOOKIE_HOST = 'https://www.youtube-nocookie.com';

export class YouTube extends Component<BaseReactPlayerProps> {
  static defaultProps = defaultProps;

  static canPlay = canPlay.youtube;

  callPlayer = callPlayer;

  getID(url) {
    if (!url) {
      return null;
    }
    return url.match(MATCH_URL_YOUTUBE)[1];
  }

  load(url, isReady) {
    const { playerVars, embedOptions } = this.props.config.youtube;
    const id = this.getID(url);

    if (isReady && this.player) {
      this.player.cueVideoById({
        videoId: id,
        startSeconds: parseStartTime(url) || playerVars.start,
        endSeconds: parseEndTime(url) || playerVars.end
      });
      return;
    }

    getSDK(SDK_URL, SDK_GLOBAL, SDK_GLOBAL_READY, (YT) => YT.loaded).then(
      (YT) => {
        if (!this.container) return;

        const {
          onReady,
          onError,
          controls,
          muted,
          loop,
          playing,
          playsinline
        } = this.props;

        this.player = new YT.Player(this.container, {
          width: '100%',
          height: '100%',
          videoId: id,
          playerVars: {
            autoplay: playing ? 1 : 0,
            mute: muted ? 1 : 0,
            controls: controls ? 1 : 0,
            start: parseStartTime(url),
            end: parseEndTime(url),
            origin: window.location.origin,
            playsinline,
            ...playerVars
          },
          events: {
            onReady: () => {
              if (loop) {
                this.player.setLoop(true); // Enable playlist looping
              }

              onReady();
            },
            onPlaybackRateChange: (event) =>
              this.props.onPlaybackRateChange(event.data),
            onStateChange: this.onStateChange,
            onError: (event) => onError(event.data)
          },
          host: MATCH_NOCOOKIE.test(url) ? NOCOOKIE_HOST : undefined,
          ...embedOptions
        });
      },
      this.props.onError
    );
  }

  onStateChange = (event) => {
    const { data } = event;
    const {
      onPlay,
      onPause,
      onBuffer,
      onBufferEnd,
      onEnded,
      onReady,
      loop,
      config: { playerVars }
    } = this.props;
    const { PLAYING, PAUSED, BUFFERING, ENDED, CUED } =
      window[SDK_GLOBAL].PlayerState;
    if (data === PLAYING) {
      onPlay();
      onBufferEnd();
    }
    if (data === PAUSED) onPause();
    if (data === BUFFERING) onBuffer();
    if (data === ENDED) {
      if (loop) {
        if (playerVars.start) {
          this.seekTo(playerVars.start);
        } else {
          this.play();
        }
      }
      onEnded();
    }
    if (data === CUED) onReady();
  };

  play() {
    this.callPlayer('playVideo');
  }

  pause() {
    this.callPlayer('pauseVideo');
  }

  stop() {
    if (!document.body.contains(this.callPlayer('getIframe'))) return;
    this.callPlayer('stopVideo');
  }

  seekTo(amount) {
    const { playing } = this.props;
    this.callPlayer('seekTo', amount);

    if (!playing) {
      this.pause();
    }
  }

  setVolume(fraction) {
    this.callPlayer('setVolume', fraction * 100);
  }

  mute() {
    this.callPlayer('mute');
  }

  unmute() {
    this.callPlayer('unMute');
  }

  setPlaybackRate(rate) {
    this.callPlayer('setPlaybackRate', rate);
  }

  setLoop(loop) {
    this.callPlayer('setLoop', loop);
  }

  getDuration() {
    return this.callPlayer('getDuration');
  }

  getCurrentTime() {
    return this.callPlayer('getCurrentTime');
  }

  getSecondsLoaded() {
    return this.callPlayer('getVideoLoadedFraction') * this.getDuration();
  }

  ref = (container) => {
    this.container = container;
  };

  render() {
    const { display } = this.props;
    const style = {
      width: '100%',
      height: '100%',
      display
    };

    return (
      <div style={style}>
        <div ref={this.ref} />
      </div>
    );
  }
}
